import { z } from "zod";

/**
 * Common environment variable schemas shared between Next.js and Vite
 */

/**
 * Robust boolean parser that handles multiple formats:
 * - "true", "1", "yes", "on" -> true
 * - "false", "0", "no", "off", undefined, "" -> false
 */
const parseBooleanString = (val: string | undefined): boolean => {
  if (!val || val === "") return false;
  const normalized = val.toLowerCase().trim();
  return (
    normalized === "true" ||
    normalized === "1" ||
    normalized === "yes" ||
    normalized === "on"
  );
};

/**
 * Zod schema for boolean environment variables
 * Accepts: boolean OR "true"/"false", "1"/"0", "yes"/"no", "on"/"off", undefined
 * Returns: boolean
 */
export const booleanEnvSchema = z
  .union([z.boolean(), z.string()])
  .optional()
  .default(false)
  .transform((val) => {
    if (typeof val === "boolean") return val;
    return parseBooleanString(val);
  });

export const appEnvSchema = z.enum([
  "development",
  "test",
  "production",
  "preview",
]);

export const gaIdSchema = z
  .string()
  .regex(
    /^G-[A-Z0-9]{10}$/,
    "GA_ID must be a valid Google Analytics ID (G-XXXXXXXXXX)",
  )
  .optional();

export const ymIdSchema = z
  .string()
  .regex(/^\d{6,10}$/, "YM_ID must be a valid Yandex Metrica ID (6-10 digits)")
  .optional();

export const appUrlSchema = z
  .string()
  .url()
  .default("https://www.piramidaraz.ru");

export const portSchema = z.coerce.number().int().min(1).max(65535).optional();

export const skipValidationSchema = z.string().optional();

export const localeSchema = z.enum(["en", "ru", "pt"]);

export const multilingualSchema = booleanEnvSchema;

/**
 * Client-side environment variables schema
 */
export const clientEnvSchema = {
  VITE_APP_ENV: appEnvSchema,
  VITE_GA_ID: gaIdSchema,
  VITE_YM_ID: ymIdSchema,
  VITE_APP_URL: appUrlSchema,
  VITE_BE_URL: appUrlSchema,
  VITE_TEST_ONLY_FOR_PROMO: booleanEnvSchema,
  VITE_DEFAULT_LOCALE: localeSchema,
  VITE_MULTILINGUAL: multilingualSchema,
  VITE_SUPER_USER_SECRET: z.string().optional(),
};

/**
 * Server-side environment variables schema
 */
export const serverEnvSchema = {
  AUTH_SECRET: z.string().optional(),
  BREVO_API_KEY: z.string().min(1, "BREVO_API_KEY is required"),
  APP_URL: appUrlSchema,
  DB_URL: z.string().url("DB_URL must be a valid database URL"),
  APP_ENV: appEnvSchema,
  SUPER_USER_SECRET: z.string().optional(),
  PROMO_GENERATE_SECRET: z.string().optional(),
  OPENAI_API_KEY: z.string().min(1, "OPENAI_API_KEY is required"),
  SENTRY_AUTH_TOKEN: z.string().optional(),
  TEST_ONLY_FOR_PROMO: booleanEnvSchema,
  SKIP_ENV_VALIDATION: skipValidationSchema,
} as const;
